import { ActivatedRoute, NavigationEnd, Params, Router, RouterEvent } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { first, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { environment } from '@environments/environment';

import { ConfigureAPIService } from '@app/configure/services/configure-api.service';
import { ScriptLoaderService } from '@app/core/services/script-loader.service';
import { ConfigureService } from '@app/configure/services/configure.service';
import { TrackingService } from '@app/core/services/tracking.service';
import { SessionService } from '@app/core/services/session.service';
import { StorageService } from '@app/core/services/storage.service';
import { PusherService } from '@app/core/services/pusher.service';
import { ModalService } from '@app/core/services/modal.service';
import { ErrorService } from '@app/core/services/error.service';

import { DomainHelper } from '@app/core/helpers/domain.helper';

import { FinanceQuote } from '@app/core/models/finance-quote.model';
import { FinanceParams } from '../models/finance-params.model';
import { Dealer } from '@app/core/models/dealer.model';

@Component({
	selector: 'cng-configure',
	templateUrl: './configure.component.html',
	styleUrls: ['../../ui/styles/footer.less', './configure.component.less'],
})
export class ConfigureComponent implements OnInit, OnDestroy {
	//  COMPONENT VARIABLES

	dealer: Dealer = this.session_service.getDealer();

	mode: 'vehicle' | 'enquiry';
	vehicle_id: number;
	enquiry_hash: string;

	has_error: boolean = false;
	finance_available: boolean = false;
	finance_failed: boolean = false;
	cashback_deal: boolean = false;
	has_fixed_footer: boolean = false;
	registration: string = null;
	finance_quote: FinanceQuote;
	full_payment_made = this.configure_service.hasFullPayment();

	soft_error: any = null;

	steps: Array<string> = ['Confirm your vehicle', 'Add part exchange', 'Customise add-on products', 'Reserve your new vehicle!'];

	destroyed$: Subject<boolean> = new Subject<boolean>();

	/**
	 * Constructs class and injects dependencies.
	 *
	 * @param {ConfigureService} configure_service
	 * @param {ConfigureAPIService} configure_api_service
	 * @param {SessionService} session_service
	 * @param {ErrorService} error_service
	 * @param {ActivatedRoute} activated_route
	 * @param {Router} router
	 * @param {PusherService} pusher_service
	 * @param {ModalService} modal_service
	 * @param {TrackingService} tracking_service
	 * @param storage_service
	 * @param script_loader_service
	 */
	constructor(
		private configure_service: ConfigureService,
		private configure_api_service: ConfigureAPIService,
		private session_service: SessionService,
		private error_service: ErrorService,
		private activated_route: ActivatedRoute,
		private router: Router,
		private pusher_service: PusherService,
		private modal_service: ModalService,
		private tracking_service: TrackingService,
		private storage_service: StorageService,
		private script_loader_service: ScriptLoaderService
	) {
		// TODO add switch for VC in engage and check that instead of 887
		if (this.dealer.id === 887 && this.dealer.visitor_chat_enabled) {
			const config: any = {
				// id: this.dealer.domain,
				id: environment.name !== 'production' ? environment.visitor_chat_domain : DomainHelper.getDomain(),
				name: this.dealer.friendly_name,
				accountId: environment.visitor_chat_account_id,
			};
			console.log(config);

			const script: HTMLScriptElement = document.createElement('script');
			script.innerHTML = 'var VC_SETTINGS = ' + JSON.stringify(config);
			document.body.appendChild(script);

			this.script_loader_service.load({ name: 'visitorchat' });
		}
	}

	/**
	 * Lifecycle hook: Subscribes to route, populates data.
	 */
	ngOnInit(): void {
		this.activated_route.queryParams.pipe(first()).subscribe((params: Params) => {
			this.tracking_service.setParams(params);

			if (params['journey']) {
				this.storage_service.setCookie('journey', params['journey']);
			}

			if (params['referrer']) {
				this.tracking_service.trackReferrer(params['referrer']);
			}

			let finance_params: FinanceParams = null;

			if (params['finance_deposit']) {
				finance_params = finance_params || {};
				finance_params.deposit = parseFloat(params['finance_deposit']);
			}

			if (params['finance_term']) {
				finance_params = finance_params || {};
				finance_params.term = parseInt(params['finance_term']);
			}

			if (params['finance_facility_type']) {
				finance_params = finance_params || {};
				finance_params.facility_type = params['finance_facility_type'];
			}

			if (params['finance_monthly_payment']) {
				finance_params = finance_params || {};
				finance_params.monthly_payment = parseFloat(params['finance_monthly_payment']);
			}

			if (params['finance_annual_mileage']) {
				finance_params = finance_params || {};
				finance_params.annual_mileage = parseInt(params['finance_annual_mileage']);
			}

			if (finance_params) {
				this.session_service.setFinanceParams(finance_params);
			}
		});

		this.activated_route.params.pipe(takeUntil(this.destroyed$)).subscribe((params: Params) => {
			if (params['vehicle_id']) {
				this.mode = 'vehicle';
				this.vehicle_id = params['vehicle_id'];
			} else {
				this.mode = 'enquiry';
				this.enquiry_hash = params['enquiry_hash'];
			}
		});

		this.activated_route.data.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
			data = data[0];

			if (data !== null && data[0] !== null) {
				this.session_service.stopAppLoading();

				// Set customer to pex enquiry data if vehicle mode and pex_enquiry_hash
				if (this.mode === 'vehicle' && data[2] !== undefined) {
					data[0].meta.customer = data[2].meta.customer;

					this.configure_service.setPexEnquiryHash(data[2].meta.enquiry_hash);
				}

				this.configure_service.setData(data[0], true);
				const vehicle = data[0].steps.purchase_vehicle.data;
				this.registration = vehicle.registration;
				this.session_service.setTitle(
					vehicle.make + ' ' + vehicle.model + (vehicle.registration ? ' - ' + vehicle.registration.toUpperCase() : '')
				);
				this.finance_available = data[0].meta.finance.available;
				this.finance_failed = data[0].meta.finance.failed;
				this.cashback_deal = data[0].meta.cashback_deal;

				if (this.mode === 'vehicle') {
					if (!this.tracking_service.getParams()['utm_source'] && 'referrer' in document && document.referrer) {
						this.tracking_service.trackReferrerWithoutSource(document.referrer);
					}

					this.modal_service.show(['configure/welcome'], {
						vehicle_id: this.vehicle_id,
					});
				} else if (this.hasFinanceApplicationUpdate(data[0].steps.finance_options.data)) {
					setTimeout(() => {
						this.showFinanceApplicationUpdate();
					}, 200);
				} else {
					this.modal_service.close();
				}

				const utm_medium: string = this.tracking_service.getParams()['utm_medium'];

				if (this.mode === 'enquiry' && !!utm_medium) {
					this.configure_service.track('entry', { medium: utm_medium }).subscribe();
				} else if (this.mode === 'enquiry' && this.storage_service.getItem('trackingQueryParams')) {
					const queryParams: any = JSON.parse(this.storage_service.getItem('trackingQueryParams'));

					if (!!queryParams['utm_medium']) {
						this.configure_service
							.track('entry', {
								medium: queryParams['utm_medium'],
							})
							.subscribe();
					}

					this.storage_service.removeItem('trackingQueryParams');
				}
			} else {
				this.has_error = true;
			}
		});

		this.configure_service.configure_data$.pipe(takeUntil(this.destroyed$)).subscribe((configure) => {
			this.finance_available = configure.meta.finance.available;
			this.finance_failed = configure.meta.finance.failed;
			this.cashback_deal = configure.meta.cashback_deal;

			this.full_payment_made = this.configure_service.hasFullPayment();
		});

		this.router.events.pipe(takeUntil(this.destroyed$)).subscribe((routerEvent: RouterEvent) => {
			if (routerEvent instanceof NavigationEnd) {
				this.checkForFooter();
			}
		});

		this.error_service.error$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
			this.has_error = true;
		});

		this.checkForFooter();
		this.initPusher();
	}

	/**
	 * Checks if deal has any processed or approved finance quotes.
	 *
	 * @param {FinanceQuote[]} quotes
	 * @returns {boolean}
	 */
	hasFinanceApplicationUpdate(quotes: FinanceQuote[]): boolean {
		if (this.finance_available && quotes.length) {
			for (const quote of quotes) {
				if (
					quote.quote_status === 'Approved' ||
					quote.quote_status === 'ConditionallyApproved' ||
					quote.quote_status === 'Processed' ||
					quote.quote_status === 'Submitted'
				) {
					this.finance_quote = quote;
					return true;
				}
			}
		}

		return false;
	}

	/**
	 * Shows finance application modal via modal service.
	 */
	showFinanceApplicationUpdate(): void {
		this.modal_service.show(['configure/finance-status'], {
			quote: this.finance_quote,
			enquiry_hash: this.enquiry_hash,
			vehicle: this.configure_service.configure_data.steps.purchase_vehicle.data,
		});
	}

	/**
	 * Initialises sockets (Pusher)
	 */
	initPusher(): void {
		if (this.mode === 'enquiry') {
			const pusherConfig: any = {
				auth: {
					headers: {
						Domain: this.session_service.domain,
					},
					params: {
						hash: this.enquiry_hash,
					},
				},
				authEndpoint: environment.api_url + 'configure/broadcasting/auth',
			};

			this.pusher_service.setConfig(pusherConfig);

			this.pusher_service.join('enquiry.' + this.enquiry_hash).listen('Updated', (event) => {
				this.configure_api_service.getConfigureData(this.enquiry_hash).subscribe((response) => {
					switch (response.status) {
						case 200:
							// TODO: Move to API
							response.body.steps.finance_options.name = 'Payment Options';

							this.configure_service.setData(response.body, true);
							return;
						case 401:
							this.router.navigate(['/configure/e', this.enquiry_hash, 'authenticate'], {
								queryParamsHandling: 'merge',
							});
							return;
						case 410:
							return null;
						default:
							this.error_service.show(
								response.status,
								response,
								'app/core/resolvers/resolvers :: getEnquiry(' + this.enquiry_hash + ')'
							);
							return null;
					}
				});
			});

			this.pusher_service.join('enquiry.' + this.enquiry_hash).listen('FinanceQuoteUpdated', (event) => {
				this.configure_service.configure_data.meta.finance.locked = true;

				const quotes: FinanceQuote[] = this.configure_service.configure_data.steps.finance_options.data.map((quote) => {
					if (quote.quote_UID === event.quote.quote_UID) {
						quote = event.quote;
						quote.selected = true;
					} else {
						quote.selected = false;
					}

					return quote;
				});

				this.configure_service.configure_data.steps.finance_options.data = quotes;

				this.configure_service.setData();

				this.finance_quote = event.quote;
				this.showFinanceApplicationUpdate();
			});
		}
	}

	/**
	 * Checks route to determine whether to show fixed footer.
	 */
	checkForFooter(): void {
		if (this.mode === 'enquiry') {
			const route: string = this.activated_route.firstChild.url['value'][0].path;
			this.has_fixed_footer = route === 'customise-review' || route === 'finance-options';
		}
	}

	/**
	 * Destroys component and any open subscriptions.
	 */
	ngOnDestroy(): void {
		this.destroyed$.next(true);
	}
}
